"use client";
import PrimaryBtn from "@/components/PrimaryBtn";
import { Icons } from "@/app/assets/shared";
import { data, AwardItem } from "../../app/main-header/data";
import QuoteButton from "@/components/QuoteButton";
import AwardCard from "./AwardCard";
import { useEffect, useRef, useState } from "react";

interface HeadingSlide {
  heading: string;
  words: string[];
  desc: string;
  href: string;
}

interface HeadBannerProps {
  buttonTitle: string;
  headings?: HeadingSlide[];
  /**
   * Legacy props from before the multi-slide rotation existed. Still
   * accepted so existing call sites (like the CMS-driven bannerSection data)
   * keep compiling, but they are no longer used to build slides — pass
   * `headings` instead to override the default rotation.
   */
  heading?: string;
  desc?: string;
}

const SLIDE_DURATION = 5000; // ms per heading

/**
 * Each slide carries its own heading, description, and CTA link — swapping
 * the active slide updates all three together (heading/words, paragraph,
 * and where the "buttonTitle" button points to).
 */
const DEFAULT_SLIDES: HeadingSlide[] = [
  {
    heading: "Engineering AI-Native",
    words: ["Digital Solutions"],
    desc: "We empower organizations with AI-driven digital solutions that accelerate growth, streamline operations, and drive meaningful digital transformation.",
    href: "/ai-development-company",
  },
  {
    heading: "Transforming AI",
    words: ["MVPs for Production"],
    desc: "We transform AI-built MVPs into scalable, secure, production-ready products designed for real-world performance and sustainable growth.",
    href: "/ai-mvp-production-ready",
  },
  {
    heading: "Drive Innovation with",
    words: ["Mobile", "Applications"],
    desc: "We develop scalable mobile products that elevate customer engagement, simplify experiences, and unlock new opportunities for business growth and innovation.",
    href: "/mobile-application-development",
  },
  {
    heading: "Building Experiences",
    words: ["That Drive Growth"],
    desc: "Our web and software solutions help businesses modernize operations, strengthen digital capabilities, and adapt faster to evolving market demands.",
    href: "/web-development",
  },
];

export default function index({
  headings,
  buttonTitle,
  // Accepted for backward compatibility with existing call sites (e.g. CMS
  // bannerSection data); intentionally unused now that slides are static.
  heading: _legacyHeading,
  desc: _legacyDesc,
}: HeadBannerProps) {
  // Prefer an explicit `headings` array; otherwise always use the default
  // 4-slide rotation. A legacy `heading`/`desc` prop pair (from before this
  // rotation existed) no longer collapses this into a single static slide —
  // pass `headings` explicitly if you want to override the defaults.
  const slides: HeadingSlide[] =
    headings && headings.length > 0 ? headings : DEFAULT_SLIDES;

  const [activeIndex, setActiveIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (slides.length <= 1) return;

    const interval = setInterval(() => {
      setIsFading(true);
      timeoutRef.current = setTimeout(() => {
        setActiveIndex((prev) => (prev + 1) % slides.length);
        setIsFading(false);
      }, 300); // fade-out duration before swapping text
    }, SLIDE_DURATION);

    return () => {
      clearInterval(interval);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [slides.length]);

  const activeSlide = slides[activeIndex];
  const HeadingTag = activeIndex === 0 ? "h1" : "span";

  const headingClass = `self-stretch justify-start text-white text-[length:var(--banner-title)] font-normal clash-font capitalize leading-[100%] transition-opacity duration-300 ease-out ${
    isFading ? "opacity-0" : "opacity-100"
  }`;
  const paragraphClasses = `self-stretch justify-start text-white text-[length:var(--banner-title)] font-normal clash-font capitalize leading-[100%] transition-opacity duration-300 ease-out ${
    isFading ? "opacity-0" : "opacity-100"
  }`;
  const descClass = `justify-start text-white/50 text-[length:var(--banner-para)] max-w-[60ch] font-normal leading-[150%] tracking-tight py-4 transition-opacity duration-300 ease-out ${
    isFading ? "opacity-0" : "opacity-100"
  }`;

  return (
    <>
      <div className="flex flex-col gap-2 mt-auto text-center lg:text-left items-center lg:items-start">
        <HeadingTag className="flex flex-col justify-start w-full gap-2 text-center lg:text-left items-center lg:items-start mt-auto m-0 p-0">
          <span className={headingClass}>{activeSlide.heading}</span>
          {/*
            Words row: on mobile this now wraps (flex-wrap) and centers
            (justify-center) so a multi-word slide like
            ["Mobile", "Applications"] breaks cleanly onto its own
            centered line instead of overflowing or leaving a stray gap
            from the unwrapped flex row. gap-y-1 gives wrapped lines
            breathing room. On lg+ it goes back to a single non-wrapping
            row with the original left-aligned spacing, unchanged from
            before.
          */}
          <span className="flex flex-wrap items-center justify-center gap-x-1 gap-y-1 lg:flex-nowrap lg:justify-start lg:gap-x-6 lg:gap-y-0">
            {activeSlide.words.map((word) => (
              <span key={word} className={paragraphClasses}>
                {word}
              </span>
            ))}
          </span>
        </HeadingTag>

        <p className={descClass}>{activeSlide.desc}</p>

        {/* New Request a Quote Button - Hidden above 1720px */}
        <div className="flex flex-col md:flex-row gap-1 md:gap-3">
          <PrimaryBtn
            text={buttonTitle}
            iconSrc={Icons?.chevronBlue}
            href={activeSlide.href}
          />
        </div>
      </div>

      <div className="flex flex-col lg:flex-row justify-between items-center mt-auto pt-4 pb-10">
        <div className="awards-content flex flex-col w-full lg:w-auto items-center text-center lg:text-left lg:items-start">
          <p className="justify-start text-white text-[length:var(--banner-para)] max-w-[60ch] font-semibold  leading-[150%] tracking-tight">
            15+ Industry Prestigious Awards
          </p>
          <p className="opacity-80 justify-start text-white text-[length:var(--primarybtn)] font-normal  leading-[120%] tracking-tight">
            for excellence and innovation
          </p>
        </div>

        <div className="awards grid grid-cols-2 gap-y-8 gap-x-4 md:flex md:flex-wrap md:justify-center md:gap-8 lg:gap-4 pt-4 lg:pt-0 mx-auto lg:mr-0 max-w-full">
          {data?.map((item: AwardItem, index: number) => (
            <AwardCard key={index} award={item} />
          ))}
        </div>
      </div>

      {/* Heading rotation indicator: equal-width slides spanning full viewport width */}
      {slides.length > 1 && (
        <div className="relative left-1/2 -translate-x-1/2 hidden lg:flex w-screen gap-1">
          {slides.map((_, index) => (
            <button
              key={index}
              type="button"
              aria-label={`Show heading ${index + 1}`}
              onClick={() => {
                if (timeoutRef.current) clearTimeout(timeoutRef.current);
                setIsFading(false);
                setActiveIndex(index);
              }}
              className="relative h-[3px] flex-1 bg-white/20 overflow-hidden"
            >
              <span
                className="absolute left-0 top-0 h-full bg-white"
                style={{
                  width:
                    index < activeIndex
                      ? "100%"
                      : index > activeIndex
                        ? "0%"
                        : undefined,
                  animation:
                    index === activeIndex
                      ? `headSlideFill ${SLIDE_DURATION}ms linear forwards`
                      : undefined,
                }}
              />
            </button>
          ))}
        </div>
      )}

      <style jsx>{`
        @keyframes headSlideFill {
          from {
            width: 0%;
          }
          to {
            width: 100%;
          }
        }
      `}</style>
    </>
  );
}
